import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Car,
  User as UserIcon,
  Warning,
  ArrowSquareOut,
  Calendar,
  Receipt,
  CaretRight,
  CheckCircle,
  Clock,
} from "@phosphor-icons/react";
import api, { API } from "../lib/api";

/**
 * Public client portal — /mi-cuenta/:token
 * One link per client. Shows total balance across all their receivables,
 * per-vehicle cards, an upcoming-payment banner, and a scrollable feed of the
 * client's 30 most recent payments (with a per-payment PDF receipt).
 * No auth required; the URL token IS the credential (1-year TTL).
 */
export default function ClientPortalPage() {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true, data: null, error: null });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/public/client-portal/${token}`);
        if (!cancelled) setState({ loading: false, data, error: null });
      } catch (err) {
        const status = err.response?.status;
        if (!cancelled) setState({
          loading: false, data: null,
          error: {
            status,
            message:
              status === 410 ? "Este portal ya expiró." :
              status === 404 ? "Portal no disponible o eliminado." :
              err.response?.data?.detail || "No se pudo cargar tu portal.",
          },
        });
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  if (state.loading) {
    return (
      <FullScreen>
        <div className="text-white/60 font-mono text-sm">Cargando tu portal...</div>
      </FullScreen>
    );
  }

  if (state.error) {
    return (
      <FullScreen>
        <div className="max-w-md rounded-2xl border border-red-500/40 bg-red-500/10 p-6 text-center">
          <Warning size={36} weight="fill" className="mx-auto mb-2 text-red-400" />
          <div className="text-lg font-heading font-bold text-white mb-1">Portal no disponible</div>
          <div className="text-[12px] font-mono text-white/60">{state.error.message}</div>
          <div className="text-[11px] font-mono text-white/55 mt-3">
            Pídele al vendedor que te envíe un nuevo enlace.
          </div>
        </div>
      </FullScreen>
    );
  }

  const { data } = state;
  const name = data.client_display_name || "Cliente";
  const s = data.summary || {};
  const owed = s.total_balance || 0;
  // Reconcile hero math: cargos adicionales (mora/intereses) are part of what
  // the client owes but were NOT part of the original contract amount. Fold
  // them into the "total to pay" so pagados + saldo = total-a-pagar.
  const totalToPay = (s.total_contract || 0) + (s.total_charges || 0);
  const paidPct = totalToPay > 0
    ? Math.min(100, Math.round(100 * (s.total_collected || 0) / totalToPay))
    : 0;
  const isSettled = owed <= 0.01;
  const hasCharges = (s.total_charges || 0) > 0.01;

  return (
    <div className="min-h-screen w-full bg-gradient-to-br from-[#0B0C13] via-[#0F1017] to-[#1A1B26] pb-20">
      <header className="border-b border-white/8 bg-black/40 backdrop-blur-sm px-4 py-3 sticky top-0 z-30">
        <div className="max-w-2xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded bg-[#FACC15] flex items-center justify-center">
              <UserIcon size={16} weight="fill" className="text-slate-900" />
            </div>
            <div>
              <div className="text-[9px] font-mono uppercase tracking-widest text-[#FACC15]">
                MI CUENTA · PORTAL PRIVADO
              </div>
              <div className="text-[12px] font-heading font-bold text-white leading-tight">
                {name}
              </div>
            </div>
          </div>
          {isSettled ? (
            <Pill green>
              <CheckCircle size={10} weight="fill" /> AL DÍA
            </Pill>
          ) : (
            <Pill amber>
              <Clock size={10} weight="fill" /> ACTIVO
            </Pill>
          )}
        </div>
      </header>

      <main className="max-w-2xl mx-auto p-4 space-y-4">
        {/* Total balance hero */}
        <section
          data-testid="portal-hero"
          className={`rounded-2xl border p-6 text-center ${isSettled
            ? "border-emerald-500/40 bg-emerald-500/8"
            : "border-[#FACC15]/40 bg-[#FACC15]/5"}`}
        >
          <div className="text-[10px] font-mono uppercase tracking-widest text-white/60 mb-1">
            {isSettled ? "Cuenta liquidada" : "Saldo pendiente"}
          </div>
          <div className={`text-5xl sm:text-6xl font-heading font-bold tabular-nums my-2 ${isSettled ? "text-emerald-300" : "text-white"}`}>
            {money(owed)}
          </div>
          <div className="text-[11px] font-mono text-white/60">
            {s.vehicle_count || 0} vehículo{s.vehicle_count === 1 ? "" : "s"} · {s.payment_count || 0} pagos registrados
          </div>
          {!isSettled && (
            <>
              <div className="mt-4 h-2 w-full bg-white/10 rounded-full overflow-hidden">
                <div
                  className="h-full bg-emerald-500 rounded-full transition-all"
                  style={{ width: `${paidPct}%` }}
                />
              </div>
              <div className="text-[10px] font-mono text-white/55 mt-1 tabular-nums flex justify-between">
                <span>{money(s.total_collected)} pagados</span>
                <span>{paidPct}% del total</span>
              </div>
              {hasCharges && (
                <div className="text-[10px] font-mono text-white/50 mt-1 text-center">
                  Incluye <span className="text-amber-300">{money(s.total_charges)}</span> en cargos por mora/interés
                </div>
              )}
            </>
          )}
        </section>

        {/* Next due (or overdue) */}
        {data.next_due && !isSettled && (
          <section
            data-testid="portal-next-due"
            className={`rounded-2xl border p-4 flex items-center gap-3 ${
              data.next_due.is_overdue
                ? "border-red-500/40 bg-red-500/8"
                : "border-white/10 bg-black/30"
            }`}
          >
            <div className={`w-10 h-10 rounded-lg border flex items-center justify-center shrink-0 ${
              data.next_due.is_overdue
                ? "bg-red-500/15 border-red-500/40"
                : "bg-[#FACC15]/15 border-[#FACC15]/40"
            }`}>
              {data.next_due.is_overdue
                ? <Warning size={18} weight="fill" className="text-red-400" />
                : <Calendar size={18} weight="fill" className="text-[#FACC15]" />}
            </div>
            <div className="flex-1 min-w-0">
              <div className={`text-[9px] font-mono uppercase tracking-widest ${
                data.next_due.is_overdue ? "text-red-300" : "text-white/40"
              }`}>
                {data.next_due.is_overdue ? "Pago vencido" : "Próximo vencimiento"}
              </div>
              <div className="text-[15px] font-heading font-bold text-white">
                {money(data.next_due.next_due_amount || data.next_due.agreed_payment)} · {data.next_due.next_due_date}
              </div>
              <div className="text-[10px] font-mono text-white/50 mt-0.5">
                {`${data.next_due.vehicle_year || ""} ${data.next_due.vehicle_make || ""} ${data.next_due.vehicle_model || ""}`.trim() || "—"}
              </div>
            </div>
          </section>
        )}

        {/* Vehicles */}
        <section data-testid="portal-vehicles" className="space-y-3">
          <div className="flex items-center justify-between px-1">
            <div className="text-[10px] font-mono uppercase tracking-widest text-white/45 flex items-center gap-1.5">
              <Car size={12} weight="bold" /> Tus vehículos
            </div>
            <div className="text-[10px] font-mono text-white/40">
              {data.vehicles.length} en cuenta
            </div>
          </div>
          {data.vehicles.map((v) => (
            <VehicleCard key={v.receivable_id} v={v} />
          ))}
        </section>

        {/* Recent payments */}
        {data.recent_payments.length > 0 && (
          <section data-testid="portal-payments" className="rounded-2xl border border-white/10 bg-black/30 overflow-hidden">
            <div className="px-4 py-3 border-b border-white/8 flex items-center justify-between">
              <div className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest text-white/45">
                <Receipt size={12} weight="bold" /> Pagos recientes
              </div>
              <div className="text-[10px] font-mono text-white/40">
                Últimos {data.recent_payments.length}
              </div>
            </div>
            <ul className="divide-y divide-white/6">
              {data.recent_payments.map((p) => (
                <PaymentRow key={p.payment_id} p={p} token={token} />
              ))}
            </ul>
          </section>
        )}

        <div className="text-center text-[10px] font-mono text-white/50 pt-2">
          Este portal expira {(data.expires_at || "").slice(0, 10)} · Guarda este enlace en tus favoritos.
        </div>
      </main>
    </div>
  );
}

function money(v) {
  if (v == null) return "—";
  return `$${Number(v).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function FullScreen({ children }) {
  return <div className="min-h-screen w-full bg-[#0F1017] flex items-center justify-center p-4">{children}</div>;
}

function Pill({ children, green, amber, red }) {
  const cls = green
    ? "bg-emerald-500/15 border-emerald-500/40 text-emerald-300"
    : amber
    ? "bg-[#FACC15]/15 border-[#FACC15]/40 text-[#FACC15]"
    : red
    ? "bg-red-500/15 border-red-500/40 text-red-300"
    : "bg-white/8 border-white/15 text-white/70";
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded border text-[9px] font-mono font-bold tracking-widest ${cls}`}>
      {children}
    </span>
  );
}

function VehicleCard({ v }) {
  const label = `${v.vehicle_year || ""} ${v.vehicle_make || ""} ${v.vehicle_model || ""}`.trim() || "—";
  const balance = v.current_balance || 0;
  const isPaid = balance <= 0.01 || v.computed_status === "PAGADO";
  const isOverdue = v.computed_status === "MOROSO" || v.computed_status === "VENCIDO";
  const pct = Math.min(100, Math.max(0, v.pct_paid || 0));
  return (
    <div
      data-testid={`portal-vehicle-${v.receivable_id}`}
      className="rounded-xl border border-white/10 bg-black/25 p-4"
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="min-w-0 flex-1">
          <div className="text-[14px] font-heading font-bold text-white leading-snug break-words">
            {label}
          </div>
          <div className="text-[10px] font-mono text-white/50 mt-0.5">
            {v.contract_number ? `#${v.contract_number}` : v.receivable_id}
            {v.vehicle_vin ? ` · VIN ${v.vehicle_vin}` : ""}
          </div>
        </div>
        {isPaid ? <Pill green><CheckCircle size={10} weight="fill" /> LIQUIDADO</Pill>
         : isOverdue ? <Pill red><Warning size={10} weight="fill" /> {v.computed_status}</Pill>
         : <Pill green><CheckCircle size={10} weight="fill" /> AL DÍA</Pill>}
      </div>

      <div className="grid grid-cols-3 gap-3">
        <BalCell label="Saldo" value={money(balance)} accent={isPaid ? "emerald" : "amber"} />
        <BalCell label="Pagado" value={money(v.total_collected)} accent="emerald" />
        <BalCell label="Total" value={money(v.total_contract)} />
      </div>

      {v.agreed_payment > 0 && !isPaid && (
        <div className="mt-3 text-[10px] font-mono text-white/50">
          Plan: {money(v.agreed_payment)} · {freqLabel(v.payment_frequency)}
          {v.next_due_date ? ` · próximo ${v.next_due_date}` : ""}
        </div>
      )}

      <div className="mt-3 h-1.5 w-full bg-white/6 rounded-full overflow-hidden">
        <div className="h-full bg-emerald-500 rounded-full" style={{ width: `${pct}%` }} />
      </div>
      <div className="text-[9px] font-mono text-white/40 mt-1 tabular-nums text-right">
        {pct.toFixed(1)}% pagado · {v.payment_count} pago{v.payment_count === 1 ? "" : "s"}
      </div>
    </div>
  );
}

function BalCell({ label, value, accent }) {
  const color = accent === "amber" ? "text-amber-300"
             : accent === "emerald" ? "text-emerald-300"
             : "text-white";
  return (
    <div>
      <div className="text-[8px] font-mono uppercase tracking-widest text-white/40 mb-0.5">{label}</div>
      <div className={`text-[13px] font-heading font-bold ${color} tabular-nums break-words`}>{value}</div>
    </div>
  );
}

function PaymentRow({ p, token }) {
  const pdfUrl = `${API}/public/client-portal/${token}/receipt/${p.payment_id}/pdf`;
  return (
    <li className="flex items-center gap-3 px-4 py-2.5 hover:bg-white/3 transition">
      <div className="w-8 h-8 rounded-lg bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center shrink-0">
        <span className="text-[10px] font-mono font-bold text-emerald-300">{p.seq}</span>
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <span className="text-[13px] font-heading font-bold text-emerald-300 tabular-nums">
            +{money(p.received_amount)}
          </span>
          <span className="text-[10px] font-mono text-white/55 tabular-nums">
            {p.payment_date}
          </span>
        </div>
        <div className="text-[10px] font-mono text-white/45 truncate">
          {p.vehicle_label} · {p.method || "—"}{p.reference ? ` · ${p.reference}` : ""}
          {p.extra_charge > 0 ? ` · +${money(p.extra_charge)} cargo` : ""}
        </div>
      </div>
      <a
        data-testid={`portal-payment-pdf-${p.payment_id}`}
        href={pdfUrl}
        target="_blank"
        rel="noreferrer"
        className="p-1.5 rounded text-white/40 hover:text-white hover:bg-white/8 transition shrink-0"
        title="Descargar recibo"
      >
        <ArrowSquareOut size={13} weight="bold" />
      </a>
    </li>
  );
}

function freqLabel(f) {
  return { weekly: "Semanal", biweekly: "Quincenal", monthly: "Mensual" }[f] || "Mensual";
}
